export interface ApiError {
  status: number;
  message: string;
}

export enum TranslationNamespace {
  LEP = 'lep',
  CONTENTFUL = 'contentful',
}

export interface ContentfulEntry {
  id: string;
  title: string;
  slug: string;
  body: string;
}

export interface TranslatedContent {
  [key: string]: ContentfulEntry;
}

export interface DropdownOption<T, V> {
  label: T;
  value: V;
}

export interface Address {
  city: string;
  country: DropdownOption<string, string>;
  primary: boolean;
  state?: DropdownOption<string, string>;
  street1: string;
  street2?: string;
  type: string;
  zipCode: string;
}

export const blankAddress: Address = {
  city: '',
  country: { label: '', value: '' },
  primary: true,
  state: { label: '', value: '' },
  street1: '',
  street2: '',
  type: 'business',
  zipCode: '',
};

export interface UserInfoForm {
  agencyAddress: Address;
  preferredLanguage: string;
  agencyName?: string;
  emailAddress: string;
  familyName: string;
  givenName: string;
  phoneNumber?: string;
  title?: string;
}

export interface RegistrationForm extends UserInfoForm {
  confirmation: boolean;
}

export const blankUserInfoForm: UserInfoForm = {
  agencyAddress: blankAddress,
  preferredLanguage: '',
  agencyName: '',
  emailAddress: '',
  familyName: '',
  givenName: '',
  phoneNumber: '',
  title: '',
};

export const blankRegistrationForm: RegistrationForm = {
  ...blankUserInfoForm,
  confirmation: false,
};

export interface IdentifierPayload {
  identifier: string;
  type: IdentifierType;
  mop: string;
  encryptedMop?: string;
  mopType: DropdownOption<string, MopType>;
  accountNumber: string;
  routingNumber: string;
  branchNumber: string;
  digit: string;
}

export interface MemberIdPayload {
  atLeastOne: boolean;
  emailAddresses: string[];
  identifierPayloads: IdentifierPayload[];
  phoneNumbers: string[];
  otherIncluded: boolean;
  other: string;
}

export interface EmergencyForm {
  emailAddress: string;
  eventDate: string; // Format: YYYY-MM-DD, e.g. "2025-06-01"
  description1: string;
  description2: string;
  description3: string;
  description4: string;
  memberIdPayload: MemberIdPayload;
  signatureAgreement: boolean;
  signature: string;
  consentDate: string; // ISO 8601 format, e.g. "2025-06-12T21:17:39.327Z"
  signatureToMatch: string;
  userInfo: UserInfoForm;
  requestType: string;
}

// Alternative with more specific types and enums
export enum MopType {
  PAYPAL = 'PAYPAL',
  CC = 'CC',
  EU_DIRECT_DEBIT = 'EU_DIRECT_DEBIT',
  US_BA = 'US_BA',
  BR_BA = 'BR_BA',
  GIFT_CARD = 'GIFT_CARD',
}

export enum IdentifierType {
  MOP = 'MOP',
  GIFT_CARD = 'GIFT_CARD'
}

export const blankEmergencyForm: EmergencyForm = {
  emailAddress: '',
  eventDate: '',
  description1: '',
  description2: '',
  description3: '',
  description4: '',
  memberIdPayload: {
    atLeastOne: false,
    emailAddresses: [],
    identifierPayloads: [],
    phoneNumbers: [],
    otherIncluded: false,
    other: ''
  },
  signatureAgreement: false,
  signature: '',
  consentDate: new Date().toISOString(),
  signatureToMatch: '',
  userInfo: blankUserInfoForm,
  requestType: ''
}

export type FeedbackVariant = 'announcement' | 'error' | 'regular' | 'success' | 'warning';

export enum MOPTypes {
  CC = 'mop_type_cc',
  EU_DIRECT_DEBIT = 'mop_type_iban',
  US_BA = 'mop_type_bank',
  PAYPAL = 'mop_type_paypal',
  GIFT_CARD = 'mop_type_gift',
  BR_BA = 'mop_type_bank_br',
}
